import React, { useContext, useEffect } from 'react'
import './index.scss'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import AddBulkAmendmentTableInfo from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/BulkAmendmentTable/AddBulkAmendmentTableInfo'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import NakedButton from '@Components/Buttons/NakedButton'
import ModalPortal from '@Components/Popups/ModalPortal'
import BulkAmendmentConfirmationPopup from '@Components/Popups/ConfirmationPopup/BulkAmendmentConfirmationPopup'
import { WorkModel } from '@Entities/work/work'
import { observer } from 'mobx-react'
import { cloneDeep } from 'lodash'
import BulkAmendmentTableFooter from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/BulkAmendmentTable/BulkAmendmentTableFooter'
import { OverclaimBulkActionType } from '@App/stores/overclaim/overclaim-search-bulk-action-store'

export interface GroupedOverclaims {
    key: string
    groupedOverclaimHeaderIds: number[]
    claimedShares: number
    initialClaimedShares: number
    publisherInfo: {
        name: string
        adminName: string
        combinedName: string
    }
    allowShareAmendment: boolean
}

const BulkAmendmentTable = () => {
    const { overclaimSearchStore, overclaimSearchBulkActionStore, rightsholderStore, preloaderStore } =
        useContext<RootStore>(StoresContext)

    const [displayNextButton, setDisplayNextButton] = React.useState(false)
    const [displaySubmissionPopup, setDisplaySubmissionPopup] = React.useState(false)

    const returnToAmendmentSelection = () => {
        overclaimSearchBulkActionStore.resetAmendmentTable()
    }

    // Pulls the selected works with only the current member's publishers
    useEffect(() => {
        if (overclaimSearchBulkActionStore.amendmentWorkList.size === 0) {
            setupAmendmentWorkList()
        }
    }, [overclaimSearchStore.worksRightsHolder, rightsholderStore.currentRightsholderId])

    const setupAmendmentWorkList = () => {
        if (!overclaimSearchStore.worksRightsHolder || overclaimSearchStore.worksRightsHolder.works.length === 0) {
            return
        }
        const worksRightsholderClone = cloneDeep(overclaimSearchStore.worksRightsHolder)
        const initialWorkList = worksRightsholderClone.works
            .filter(
                (work) =>
                    work.overclaimHeaderId &&
                    overclaimSearchBulkActionStore.idsSelectedForBulkAction.has(work.overclaimHeaderId)
            )
            .map((work) => ({
                ...work,
                originalPublishers: work.originalPublishers.filter(
                    (pub) => pub.topMemberIpId === rightsholderStore.currentRightsholderId
                ),
            }))
        const workList = new Map<number, WorkModel>(
            initialWorkList
                .filter((work) => work.overclaimHeaderId != null)
                .map((work) => [work.overclaimHeaderId!, { ...work }])
        )
        overclaimSearchBulkActionStore.setAmendmentWorkList(workList)
    }

    // Groups works on a per publisher-chain basis with the same claimed shares and publisher/admin names
    useEffect(() => {
        if (overclaimSearchBulkActionStore.groupedOverclaimsForAmendment.size === 0) {
            groupOverclaims()
        }
    }, [])

    const groupOverclaims = () => {
        const newGroupedOverclaims = new Map<string, GroupedOverclaims>()
        Array.from(overclaimSearchBulkActionStore.amendmentWorkList.values()).forEach((work) => {
            work.originalPublishers.forEach((pub) => {
                const publisherAdminCombo = [pub.publisherName, pub.administratorPublishers[0]?.publisherName]
                    .filter(Boolean)
                    .join('/')
                const claimedShares =
                    (pub.administratorPublishers.length !== 0
                        ? pub.administratorPublishers[0].publisherShare
                        : pub.publisherShare) || 0
                const key = `${claimedShares}|${publisherAdminCombo}`

                if (!newGroupedOverclaims.has(key)) {
                    newGroupedOverclaims.set(key, {
                        key,
                        groupedOverclaimHeaderIds: [],
                        initialClaimedShares: claimedShares,
                        claimedShares,
                        allowShareAmendment: false,
                        publisherInfo: {
                            name: pub.publisherName,
                            adminName: pub.administratorPublishers[0]?.publisherName || '',
                            combinedName: publisherAdminCombo,
                        },
                    })
                }
                const group = newGroupedOverclaims.get(key)!
                if (work.overclaimHeaderId != null) {
                    group.groupedOverclaimHeaderIds.push(work.overclaimHeaderId)
                }
            })
        })
        overclaimSearchBulkActionStore.setGroupedOverclaimsForAmendment(newGroupedOverclaims)
    }

    const updateClaimedShares = (key: string, newClaimedShares: number) => {
        const updated = cloneDeep(overclaimSearchBulkActionStore.groupedOverclaimsForAmendment)
        const group = updated.get(key)
        if (group) {
            group.claimedShares = newClaimedShares
            group.groupedOverclaimHeaderIds.forEach((headerId) => {
                const work = overclaimSearchBulkActionStore.amendmentWorkList.get(headerId)
                if (work) {
                    const selectedPublisher = work.originalPublishers.find(
                        (pub) => pub.publisherName === group.publisherInfo.name
                    )
                    if (selectedPublisher) {
                        if (selectedPublisher.administratorPublishers.length !== 0) {
                            selectedPublisher.administratorPublishers[0].publisherShare = newClaimedShares
                        } else {
                            selectedPublisher.publisherShare = newClaimedShares
                        }
                    }
                }
            })
        }
        overclaimSearchBulkActionStore.setGroupedOverclaimsForAmendment(updated)
    }

    const submitAmendments = async () => {
        preloaderStore.openTransparentPreloader(true)
        await overclaimSearchBulkActionStore
            .submitBulkAmendOverclaim(Array.from(overclaimSearchBulkActionStore.amendmentWorkList.values()))
            .finally(() => {
                setDisplaySubmissionPopup(true)
                preloaderStore.openTransparentPreloader(false)
            })
    }

    return (
        <div className="bulk-amendment-table-container">
            <ModalPortal isOpen={displaySubmissionPopup}>
                <BulkAmendmentConfirmationPopup
                    onClose={() => {
                        overclaimSearchBulkActionStore.resetSelection()
                        setDisplaySubmissionPopup(false)
                        returnToAmendmentSelection()
                        overclaimSearchBulkActionStore.setActiveBulkAction(OverclaimBulkActionType.AmendmentSelection)
                    }}
                />
            </ModalPortal>
            {overclaimSearchBulkActionStore.isAmendmentInReview ? (
                <div className="description">
                    Please review your changes before submitting. If you need to make edits, click “Back” to return to
                    the previous step.
                </div>
            ) : (
                <div className="description">
                    Update the percentage of shares you want to retain below. Please note, you can't set the amount to
                    0%. If you'd prefer to give up all your shares on a work, you can do that through the relinquishment
                    option.
                </div>
            )}
            {!overclaimSearchBulkActionStore.isAmendmentInReview && (
                <NakedButton aria-label="Cancel bulk relinquishment" onClick={returnToAmendmentSelection}>
                    {'< Back'}
                </NakedButton>
            )}

            <TablesForm
                className="bulk-amendment-table"
                tableInfos={AddBulkAmendmentTableInfo({
                    updateClaimedShares,
                    setDisplayNextButton,
                })}
                shouldFocusError={false}
                hideAddButton
            />
            <BulkAmendmentTableFooter displayNextButton={displayNextButton} submitAmendments={submitAmendments} />
        </div>
    )
}
export default observer(BulkAmendmentTable)
